import type { FormEvent } from 'react'
import type { ClientType, ResourceType } from 'types'

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { sc } from 'utils'
import copy from 'copy.json'
import { basePaths, paths } from 'paths'

import Button from 'components/Button'
import Radio, { RadioLabel } from 'components/Radio'
import Spinner from 'components/Spinner'
import Input from 'components/Input'
import FileUploadWrapper from 'components/layout/FileUploadWrapper'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { energyProfileCsvGuide } from 'components/csvFormatGuides'

import columnFormatPreview from 'assets/column-format-preview.svg'
import tableFormatPreview from 'assets/table-format-preview.svg'

type Props = {
  clientType: ClientType
  resourceType: ResourceType
}

export default function FileUploadFormatChoice({ clientType, resourceType }: Props) {
  const { api } = useStore()
  const { buyerId, sellerId, demandId, offerId } = useParams()
  const navigate = useNavigate()
  const [seriesFileType, setSeriesFileType] = useState<'DAY_HOUR_MATRIX' | 'TWO_COLUMN'>(
    'DAY_HOUR_MATRIX'
  )
  const [seriesType, setSeriesType] = useState<'HOURLY' | 'QUARTER_HOURLY'>('HOURLY')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const { search, file_upload, confirmation } = paths

  let clientId: string | undefined
  let resourceId: string | undefined

  if (clientType === 'buyer') {
    clientId = buyerId
    resourceId = demandId
  }

  if (clientType === 'seller') {
    clientId = sellerId
    resourceId = offerId
  }

  const handleFileChange = (event: FormEvent<HTMLInputElement>) => {
    const currentTarget = event.currentTarget

    if (currentTarget.files) setFile(currentTarget.files[0])
  }

  const submit = async () => {
    if (!resourceId || !file) return

    const payload = {
      series_type: seriesType,
      series_file_type: seriesFileType
    }

    const formData = new FormData()

    formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }))
    formData.append('profileFile', file)

    setLoading(true)

    const response = await api.postProcessedResourceProfileFile(
      formData,
      resourceType,
      Number(resourceId)
    )

    setLoading(false)

    if (response === null) return

    navigate(
      `${basePaths.admin_panel}/${clientType}/${search}/${clientId}/${file_upload}/${resourceId}/${confirmation}`,
      {
        state: response
      }
    )
  }

  return (
    <Background>
      <Content>
        <h1>{copy.page.file_upload_format_choice.heading}</h1>

        <FormatChoice>
          <FormatOption
            active={seriesFileType === 'DAY_HOUR_MATRIX'}
            onClick={() => setSeriesFileType('DAY_HOUR_MATRIX')}
          >
            <h2>{copy.page.file_upload_format_choice.matrix}</h2>
            <img src={tableFormatPreview} alt={copy.page.file_upload_format_choice.matrix_alt} />
            <Radio
              checked={seriesFileType === 'DAY_HOUR_MATRIX'}
              onChange={() => setSeriesFileType('DAY_HOUR_MATRIX')}
            />
          </FormatOption>

          <FormatOption
            active={seriesFileType === 'TWO_COLUMN'}
            onClick={() => setSeriesFileType('TWO_COLUMN')}
          >
            <h2>{copy.page.file_upload_format_choice.two_column}</h2>
            <img
              src={columnFormatPreview}
              alt={copy.page.file_upload_format_choice.two_column_alt}
            />
            <Radio
              checked={seriesFileType === 'TWO_COLUMN'}
              onChange={() => setSeriesFileType('TWO_COLUMN')}
            />
          </FormatOption>
        </FormatChoice>

        <Timenterval>
          <h2>{copy.page.file_upload_format_choice.time_interval}</h2>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <RadioLabel>
              <Radio
                name="seriesType"
                value="HOURLY"
                checked={seriesType === 'HOURLY'}
                onChange={() => setSeriesType('HOURLY')}
              />
              {copy.page.file_upload_format_choice.radio_hourly}
            </RadioLabel>

            <RadioLabel>
              <Radio
                name="seriesType"
                value="QUARTER_HOURLY"
                checked={seriesType === 'QUARTER_HOURLY'}
                onChange={() => setSeriesType('QUARTER_HOURLY')}
              />
              {copy.page.file_upload_format_choice.radio_quarter_hourly}
            </RadioLabel>
          </div>
        </Timenterval>

        <FileUploadWrapper style={{ flexDirection: 'column' }}>
          <h2>{copy.page.file_upload_format_choice.processed_file}</h2>
          <CsvFormatGuide definition={energyProfileCsvGuide} />
          <Input name="file" type="file" onChange={handleFileChange} accept=".csv" />
        </FileUploadWrapper>

        <ButtonsWrapper>
          <Button disabled={!file} onClick={submit}>
            {loading ? <Spinner size={16} /> : copy.common.button.next}
          </Button>

          <Button
            secondary
            onClick={() =>
              navigate(`${basePaths.admin_panel}/${clientType}/${search}/${clientId}`, {
                state: resourceId
              })
            }
          >
            {copy.common.button.close}
          </Button>
        </ButtonsWrapper>
      </Content>
    </Background>
  )
}

const Background = styled.div`
  /* display: flex; */
  align-items: center;
  justify-content: center;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background: rgba(39, 67, 47, 0.89);
  overflow-y: auto;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;

  padding: 36px 109px;
  margin: 24px;

  background: var(--white);
  border-radius: 4px;

  h1 {
    font: var(--600-24);
    text-align: center;
  }

  h2 {
    font: var(--600-16);
  }
`

const FormatChoice = styled.div`
  display: flex;
`

const FormatOption = styled.div<{ active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  border: 1px solid var(--light-gray);
  padding: 24px 12px;
  cursor: pointer;
  ${sc('active', 'border-color: var(--green);')}

  [type="radio"] {
    pointer-events: none;
  }
`

const Timenterval = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${RadioLabel} {
    border: 1px solid var(--light-gray);
    padding: 8px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  gap: 8px;
  width: 100%;
  max-width: 312px;
`
